import { LinearProgress } from '@mui/material';
import clsx from 'clsx';
import { useCustomSnackbar } from 'components/NotificationSnackbars';
import { LoadingContext, LoadingDoneVariant } from 'contexts';
import React, { useContext, useEffect } from 'react';

import useLoadingBarStyles from './styles';
import { LoadingBarProps } from './types';

// global current snackbar so we don't get multiple messages when we have multiple loading bars.
const currentSnackbar = {
  current: '',
};

const LoadingBar: React.FunctionComponent<LoadingBarProps> = (props) => {
  const { className, absolute } = props;
  const classes = useLoadingBarStyles(props);
  const { enqueueCustomSnackbar } = useCustomSnackbar();
  const { isLoading, statuses } = useContext(LoadingContext);

  const currentStatusItem = statuses[0];
  const [activeStatus, itemName] = (currentStatusItem?.status || '').split('_') as [
    LoadingDoneVariant,
    string
  ];

  useEffect(() => {
    if (!currentStatusItem?.message || currentSnackbar.current === activeStatus) {
      return;
    }

    currentSnackbar.current = activeStatus;
    enqueueCustomSnackbar(currentStatusItem.message, {
      variant: activeStatus,
      onExited: () => (currentSnackbar.current = ''),
    });
  }, [activeStatus, currentStatusItem, enqueueCustomSnackbar, itemName]);

  const renderStatus = (variant: LoadingDoneVariant) => {
    const statusClass = `status_${variant}` as const;
    const wrapperClass = `wrapper_${variant}` as const;
    if (!classes[statusClass] || !variant) {
      return null;
    }
    return (
      <React.Fragment key={`activeStatus_${itemName}`}>
        <div className={clsx(classes.statusWrapper, classes[wrapperClass])}></div>
        <div className={clsx(classes.statusBar, classes[statusClass])}></div>
      </React.Fragment>
    );
  };

  const renderBars = () => {
    return (
      <div className={clsx(classes.root, className)}>
        {renderStatus(activeStatus)}
        {isLoading && <LinearProgress classes={{ bar: classes.bar, root: classes.progressRoot }} />}
      </div>
    );
  };

  const renderLoadingBar = () =>
    absolute ? <div className={classes.absoluteWrapper}>{renderBars()}</div> : renderBars();

  return renderLoadingBar();
};

export default LoadingBar;
